// A short name with its full name on hover: a customer as `customerNamer` says (its shown name, Admin →
// Customers; else A2000 its code, Deposco its name; the original "name · code · source" on hover) and a division's abbreviation (Admin → Divisions; DIVISION on hover, each one's code when there are
// several). A Mixed line shows the first and "+N more" (customers) or each abbreviation with its
// codes (divisions), and every one on hover. Every page that shows them uses this.

import { customerFace, divisionHovers, divisionLabel, hoverRows } from "@app/shared";
import type { DivisionRef, LineView } from "@app/shared";
import { Tooltip, TooltipContent, TooltipTrigger } from "@axny/platform-ui";
import { cn } from "@/lib/utils";

export interface Named {
  text: string;
  /** The full name. */
  tip: string;
  /** The hover when it is alone, if not `tip`. */
  full?: string;
}

/** A line's customers, each as `assemble` named it (`customerFace`); a line with none: its names. */
export const customerItems = (line: Pick<LineView, "customers" | "customerNames">): Named[] =>
  line.customers.length ? line.customers.map(customerFace) : line.customerNames.map((n) => ({ text: n, tip: n }));

/** Divisions: each abbreviation; on hover one's full name, several's codes (`divisionHovers`). */
export const divisionItems = (divs: readonly DivisionRef[]): Named[] => divisionHovers(divs);

export function NamedText({
  items,
  label,
  className = "min-w-0 truncate",
}: {
  items: readonly Named[];
  /** What the cell says instead of the first and "+N more". */
  label?: string;
  className?: string;
}) {
  const list = items.filter((i) => i.text);
  if (!list.length) return <span className={className} />;
  const short = label || (list.length > 1 ? `${list[0]!.text} +${list.length - 1} more` : list[0]!.text);
  const tips = list.filter((i) => i.tip && i.tip !== i.text);
  if (list.length === 1 && !tips.length) return <span className={className}>{short}</span>;
  return (
    <Tooltip>
      <TooltipTrigger render={<span className={cn(className, "cursor-default")} />}>{short}</TooltipTrigger>
      <TooltipContent className="max-h-72 max-w-md overflow-y-auto">
        {list.length === 1 ? (
          (list[0]!.full ?? list[0]!.tip)
        ) : (
          <ul className="space-y-0.5">
            {hoverRows(list).map((row) => (
              <li key={row}>{row}</li>
            ))}
          </ul>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/** A line's divisions: "AXNY (H, TX), EGC (DA)" when several (`divisionLabel`), each "ABBR — code" on hover. */
export const DivisionText = ({ divs, className }: { divs: readonly DivisionRef[]; className?: string }) => (
  <NamedText items={divisionItems(divs)} label={divisionLabel(divs)} className={className} />
);
